import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setError("Too many reset requests. Wait a bit and try again.");
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Try again.");
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div className="hidden md:flex flex-col items-center justify-center">
          <ResmoMascot state="thinking" size={260} />
          <div className="font-display text-2xl mt-4 uppercase text-center">
            Happens to<br />the best of us.
          </div>
        </div>

        <div className="rm-card-static p-8 md:p-10">
          <div className="rm-tag mb-3">Password reset</div>

          {sent ? (
            <div data-testid="forgot-success">
              <h1 className="font-display text-4xl uppercase mb-4">Check your inbox.</h1>
              <p className="text-sm leading-relaxed mb-2" style={{ color: "#334155" }}>
                If <b style={{ color: "#0F1B2D" }}>{email}</b> has a Resmo account, a reset
                link is on its way. It's good for <b>60 minutes</b> and works once.
              </p>
              <p className="text-xs font-mono-rm mb-6" style={{ color: "#64748B" }}>
                Nothing arriving? Check spam, or make sure you used the email you registered with.
              </p>
              <Link to="/login" className="rm-btn rm-btn-secondary text-sm" data-testid="forgot-back-to-login">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl uppercase mb-2">Forgot your password?</h1>
              <p className="text-sm mb-6" style={{ color: "#475569" }}>
                Enter your account email and we'll send you a one-time reset link.
              </p>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
                <div>
                  <label className="rm-label block mb-2">Email</label>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rm-input"
                    placeholder="you@email.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                {error && (
                  <div className="font-mono-rm text-sm p-3 rounded-md"
                       style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
                       data-testid="forgot-error">
                    {error}
                  </div>
                )}
                <button type="submit" disabled={loading || !email}
                        className="rm-btn rm-btn-primary w-full disabled:opacity-50"
                        data-testid="forgot-submit-btn">
                  <Mail size={16} /> {loading ? "Sending…" : "Email me a reset link"}
                </button>
              </form>
              <div className="mt-6 text-sm font-mono-rm">
                Remembered it?{" "}
                <Link to="/login" className="font-bold underline" data-testid="forgot-to-login-link">
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
